import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';
import { API_URLS } from '../../constants/api-urls';
import CmExternalQcInspection from './CmExternalQcInspection';
import './InspectionCompletion.scss';

function isCmQaRequest(qr) {
  if (!qr) return false;
  if (qr.context_source === 'contract_manufacturer') return true;
  const a = qr.cm_rfq_id != null && Number(qr.cm_rfq_id) > 0;
  const b = qr.cm_quotation_id != null && Number(qr.cm_quotation_id) > 0;
  return a && b;
}

const DEFAULT_REPORT_ITEMS = [
  { name: 'Condition / Quality', status: 'passed', notes: '' },
  { name: 'Quantity / Specifications', status: 'passed', notes: '' },
  { name: 'Packaging & Labeling', status: 'passed', notes: '' },
  { name: 'Documentation', status: 'passed', notes: '' },
  { name: 'Overall Compliance', status: 'passed', notes: '' },
];

function cloneDefaultReportItems() {
  return DEFAULT_REPORT_ITEMS.map((item) => ({ ...item }));
}

function checklistToReportItems(checklist) {
  if (!Array.isArray(checklist) || checklist.length === 0) {
    return cloneDefaultReportItems();
  }
  return checklist.map((item) => {
    if (typeof item === 'string') return { name: item, status: 'passed', notes: '' };
    if (item && typeof item === 'object') {
      return {
        name: item.name || item.label || item.title || 'Item',
        status: 'passed',
        notes: '',
      };
    }
    return { name: 'Item', status: 'passed', notes: '' };
  });
}

function resolveReportItems({ savedReport, qaRequest, inspection }) {
  const savedItems = savedReport?.items;
  if (Array.isArray(savedItems) && savedItems.length > 0) {
    return savedItems;
  }

  const checklist =
    qaRequest?.inspection_checklist ||
    qaRequest?.scope_logistics?.inspection_checklist ||
    null;
  if (Array.isArray(checklist) && checklist.length > 0) {
    return checklistToReportItems(checklist);
  }

  const milestones = inspection?.milestones;
  if (Array.isArray(milestones) && milestones.length > 0) {
    return milestones
      .map((m) => (typeof m === 'string' ? m.trim() : ''))
      .filter(Boolean)
      .map((name) => ({ name, status: 'passed', notes: '' }));
  }

  return cloneDefaultReportItems();
}

export default function InspectionCompletion() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const submitSentRef = useRef(false);
  const [flow, setFlow] = useState('loading'); // loading | auction | qa_cm | qa_classic
  const [inspection, setInspection] = useState(null);
  const [qaRequest, setQaRequest] = useState(null);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inspectorAgencyName, setInspectorAgencyName] = useState('');
  const [inspectorDisplayName, setInspectorDisplayName] = useState('');

  const [formData, setFormData] = useState({
    overall_rating: '',
    inspection_report: {},
    inspector_recommendations: '',
  });

  const [reportItems, setReportItems] = useState(() => cloneDefaultReportItems());

  const loadInspectorProfile = useCallback(async () => {
    try {
      const user = StorageService.getUser();
      const uid = user?.user_login_id ?? user?.id;
      if (uid == null) return;
      setInspectorDisplayName(user?.name || user?.full_name || '');
      const res = await CommonService.get(API_URLS.QA.INSPECTOR_PROFILE_BY_USER(uid));
      const p = res?.data ?? res ?? {};
      setInspectorAgencyName(p.company_name || p.name || '');
    } catch (_) {
      const user = StorageService.getUser();
      setInspectorDisplayName(user?.name || '');
    }
  }, []);

  const fetchInspectionData = useCallback(async () => {
    try {
      setLoading(true);
      setFlow('loading');

      let qaInsp = null;
      try {
        const qaRes = await CommonService.get(API_URLS.QA.INSPECTION_BY_ID(inspectionId));
        if (qaRes && qaRes.success && qaRes.data) {
          qaInsp = qaRes.data;
        }
      } catch (e) {
        if (e?.response?.status !== 404) {
          console.warn('QA inspection fetch:', e?.response?.status);
        }
      }

      if (qaInsp) {
        setInspection(qaInsp);
        const qrRes = await CommonService.get(API_URLS.QA.GET_BY_ID(qaInsp.qa_request_id));
        const qr = qrRes?.data ?? null;
        setQaRequest(qr);

        await loadInspectorProfile();

        const listPrefersCm = location.state?.qaReportUi === 'cm_external';
        const useCmExternalUi =
          isCmQaRequest(qr) || (listPrefersCm && qr?.cm_rfq_id != null && Number(qr.cm_rfq_id) > 0);

        if (useCmExternalUi) {
          setFlow('qa_cm');
          setLoading(false);
          return;
        }

        setFlow('qa_classic');
        let savedReport = null;
        if (qaInsp.inspection_report != null) {
          try {
            savedReport =
              typeof qaInsp.inspection_report === 'string'
                ? JSON.parse(qaInsp.inspection_report)
                : qaInsp.inspection_report;
          } catch (_) {
            savedReport = null;
          }
        }
        setReportItems(resolveReportItems({ savedReport, qaRequest: qr, inspection: qaInsp }));
        if (savedReport || qaInsp.overall_rating || qaInsp.inspector_recommendations) {
          setFormData((prev) => ({
            ...prev,
            overall_rating: qaInsp.overall_rating || '',
            inspector_recommendations: qaInsp.inspector_recommendations || '',
          }));
        }
        setLoading(false);
        return;
      }

      const response = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION(inspectionId));
      if (response && response.success) {
        setInspection(response.data);
        setFlow('auction');

        if (response.data.inspection_report) {
          const report =
            typeof response.data.inspection_report === 'string'
              ? JSON.parse(response.data.inspection_report)
              : response.data.inspection_report;

          if (report.items) {
            setReportItems(report.items);
          }

          setFormData((prev) => ({
            ...prev,
            overall_rating: response.data.overall_rating || '',
            inspector_recommendations: response.data.inspector_recommendations || '',
          }));
        }

        if (response.data.rfq_id) {
          const rfqResponse = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ(response.data.rfq_id));
          if (rfqResponse && rfqResponse.success) {
            const rfqData = rfqResponse.data;

            if (
              !response.data.inspection_report ||
              !(typeof response.data.inspection_report === 'string'
                ? JSON.parse(response.data.inspection_report)
                : response.data.inspection_report
              ).items?.length
            ) {
              const checklist = rfqData?.inspection_checklist;
              setReportItems(checklistToReportItems(checklist));
            }

            const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${rfqData.auction_id}`);
            if (auctionResponse && auctionResponse.success) {
              setAuction(auctionResponse.data);
            }
          }
        }
      } else {
        setInspection(null);
      }
    } catch (error) {
      console.error('Error fetching inspection:', error);
      toast.error('Failed to load inspection details');
      setInspection(null);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, loadInspectorProfile, location.state]);

  useEffect(() => {
    fetchInspectionData();
  }, [fetchInspectionData]);

  const handleReportItemChange = (index, field, value) => {
    setReportItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleAddReportItem = () => {
    setReportItems((prev) => [...prev, { name: `Inspection item ${prev.length + 1}`, status: 'passed', notes: '' }]);
  };

  const handleRemoveReportItem = (index) => {
    setReportItems((prev) => {
      if (prev.length <= 1) {
        toast.info('At least one inspection item is required.');
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const renderReportItemsSection = () => (
    <div className="section-card report-items-card">
      <div className="report-items-header">
        <h3>Inspection Report Items</h3>
        <button type="button" className="btn-add-report-item" onClick={handleAddReportItem}>
          + Add item
        </button>
      </div>
      {reportItems.length === 0 ? (
        <p className="report-items-empty">No inspection items yet. Add at least one item to submit the report.</p>
      ) : null}
      {reportItems.map((item, index) => (
        <div key={index} className="report-item">
          <div className="item-name-row">
            <input
              type="text"
              className="form-control item-name-input"
              value={item.name}
              onChange={(e) => handleReportItemChange(index, 'name', e.target.value)}
              placeholder="Inspection item name"
            />
            {reportItems.length > 1 ? (
              <button
                type="button"
                className="btn-remove-report-item"
                onClick={() => handleRemoveReportItem(index)}
                aria-label="Remove inspection item"
              >
                Remove
              </button>
            ) : null}
          </div>
          <div className="item-status">
            <select
              className="form-control"
              value={item.status}
              onChange={(e) => handleReportItemChange(index, 'status', e.target.value)}
            >
              <option value="passed">Passed</option>
              <option value="minor_issues">Minor Issues</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="item-notes">
            <textarea
              className="form-control"
              placeholder="Enter notes..."
              value={item.notes}
              onChange={(e) => handleReportItemChange(index, 'notes', e.target.value)}
              rows="2"
            />
          </div>
        </div>
      ))}
    </div>
  );

  const handleSubmitAuction = async (e) => {
    e.preventDefault();

    if (submitSentRef.current) return;
    if ((inspection?.status || '').toLowerCase() === 'completed') {
      toast.info('Report already submitted.');
      return;
    }
    if (!formData.overall_rating) {
      toast.error('Please select overall rating');
      return;
    }
    if (reportItems.length === 0 || reportItems.every((item) => !String(item.name || '').trim())) {
      toast.error('Please add at least one inspection item');
      return;
    }

    try {
      submitSentRef.current = true;
      setSubmitting(true);

      const payload = {
        overall_rating: formData.overall_rating,
        inspection_report: {
          items: reportItems.map((item) => ({
            ...item,
            name: String(item.name || '').trim() || 'Inspection item',
          })),
        },
        inspector_recommendations: formData.inspector_recommendations || null,
      };

      const response = await CommonService.put(API_URLS.AUCTION.UPDATE_INSPECTION_REPORT(inspectionId), payload);

      if (response && response.success) {
        toast.success('Inspection report submitted successfully!');
        navigate('/auction/qa/dashboard');
        return;
      }
      throw new Error(response?.message || 'Failed to submit inspection report');
    } catch (error) {
      console.error('Error submitting inspection report:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit inspection report');
      submitSentRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitQaClassic = async (e) => {
    e.preventDefault();
    if (submitSentRef.current) return;
    if ((inspection?.status || '').toLowerCase() === 'completed') {
      toast.info('Report already submitted.');
      return;
    }
    if (!formData.overall_rating) {
      toast.error('Please select overall rating');
      return;
    }
    if (reportItems.length === 0 || reportItems.every((item) => !String(item.name || '').trim())) {
      toast.error('Please add at least one inspection item');
      return;
    }
    try {
      submitSentRef.current = true;
      setSubmitting(true);
      const payload = {
        overall_rating: formData.overall_rating,
        inspection_report: {
          items: reportItems.map((item) => ({
            ...item,
            name: String(item.name || '').trim() || 'Inspection item',
          })),
        },
        inspector_recommendations: formData.inspector_recommendations || null,
      };
      const response = await CommonService.put(API_URLS.QA.INSPECTION_COMPLETE(inspectionId), payload);
      if (response && response.success) {
        toast.success('Inspection report submitted successfully!');
        navigate('/auction/qa/dashboard/inspections');
        return;
      }
      throw new Error(response?.message || 'Failed to submit');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit inspection report');
      submitSentRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const isAlreadyCompleted = (inspection?.status || '').toLowerCase() === 'completed';

  if (loading) {
    return <div className="inspection-completion loading">Loading...</div>;
  }

  if (!inspection) {
    return <div className="inspection-completion error">Inspection not found</div>;
  }

  if (flow === 'qa_cm' && qaRequest) {
    return (
      <CmExternalQcInspection
        inspection={inspection}
        qaRequest={qaRequest}
        inspectorAgencyName={inspectorAgencyName}
        inspectorDisplayName={inspectorDisplayName}
        onReload={fetchInspectionData}
      />
    );
  }

  if (flow === 'qa_classic') {
    return (
      <div className="inspection-completion">
        <div className="container-fluid">
          <div className="page-header">
            <button type="button" className="back-btn" onClick={() => navigate(-1)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>
            <h1>Complete Inspection Report</h1>
            <p style={{ color: '#666', marginTop: 8 }}>Buyer–seller quotation QA (non–contract manufacturing)</p>
          </div>

          <form onSubmit={handleSubmitQaClassic}>
            <div className="form-layout">
              <div className="left-column">
                {qaRequest && (
                  <div className="section-card product-card">
                    <h3>Request</h3>
                    <div className="product-name">{qaRequest.qa_request_number || `QA-${qaRequest.qa_request_id}`}</div>
                    <div className="product-details">
                      <span>
                        Product:{' '}
                        {qaRequest.scope_logistics?.product_name || qaRequest.scope_logistics?.product_description || '—'}
                      </span>
                    </div>
                  </div>
                )}

                {renderReportItemsSection()}
              </div>

              <div className="right-column">
                <div className="section-card rating-card">
                  <h3>
                    Overall Rating <span className="required">*</span>
                  </h3>
                  <div className="rating-options">
                    {['Excellent', 'Good', 'Fair', 'Poor'].map((rating) => (
                      <label key={rating} className="rating-option">
                        <input
                          type="radio"
                          name="overall_rating"
                          value={rating}
                          checked={formData.overall_rating === rating}
                          onChange={(e) => setFormData((prev) => ({ ...prev, overall_rating: e.target.value }))}
                        />
                        <span>{rating}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="section-card recommendations-card">
                  <h3>Inspector Recommendations</h3>
                  <textarea
                    className="form-control"
                    name="inspector_recommendations"
                    rows="6"
                    placeholder="Enter recommendations..."
                    value={formData.inspector_recommendations}
                    onChange={(e) => setFormData((prev) => ({ ...prev, inspector_recommendations: e.target.value }))}
                  />
                </div>

                {isAlreadyCompleted ? (
                  <p className="already-submitted-msg">Report already submitted.</p>
                ) : (
                  <button type="submit" className="btn-submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Inspection Report'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="inspection-completion">
      <div className="container-fluid">
        <div className="page-header">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
          <h1>Complete Inspection Report</h1>
        </div>

        <form onSubmit={handleSubmitAuction}>
          <div className="form-layout">
            <div className="left-column">
              {auction && (
                <div className="section-card product-card">
                  <h3>Product Information</h3>
                  <div className="product-name">{auction.product_name}</div>
                  <div className="product-details">
                    <span>Category: {auction.category_name || 'N/A'}</span>
                    <span>
                      Final Bid:{' '}
                      {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
                        auction.winning_bid_amount || 0
                      )}
                    </span>
                  </div>
                </div>
              )}

              {renderReportItemsSection()}
            </div>

            <div className="right-column">
              <div className="section-card rating-card">
                <h3>
                  Overall Rating <span className="required">*</span>
                </h3>
                <div className="rating-options">
                  {['Excellent', 'Good', 'Fair', 'Poor'].map((rating) => (
                    <label key={rating} className="rating-option">
                      <input
                        type="radio"
                        name="overall_rating"
                        value={rating}
                        checked={formData.overall_rating === rating}
                        onChange={(e) => setFormData((prev) => ({ ...prev, overall_rating: e.target.value }))}
                      />
                      <span>{rating}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="section-card recommendations-card">
                <h3>Inspector Recommendations</h3>
                <textarea
                  className="form-control"
                  name="inspector_recommendations"
                  rows="6"
                  placeholder="Enter recommendations..."
                  value={formData.inspector_recommendations}
                  onChange={(e) => setFormData((prev) => ({ ...prev, inspector_recommendations: e.target.value }))}
                />
              </div>

              {isAlreadyCompleted ? (
                <p className="already-submitted-msg">Report already submitted. No duplicate entry will be created.</p>
              ) : (
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Inspection Report'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
